import Phaser from 'phaser';
import type { LevelDef } from '../../core/game';
import { HUB_MENU_TRACK } from '../../config/levels';
import {
  ORIGIN_CHAPTERS,
  darkCoinRewardFor,
  findOriginChapter,
  isOriginLevelUnlocked,
  lightCoinRewardFor,
  originCurrencyOf,
  type OriginChapterDef,
} from '../../config/originEvent';
import { originArenaFor } from '../../config/testArena';
import { LocalScoreService } from '../../services/scoreService';
import { ProgressService } from '../../services/progressService';
import { saveStorage } from '../saveStorage';
import { drawStarfield } from '../starfield';
import { DARKCOIN_SPIN_FRAMES, LIGHTCOIN_SPIN_FRAMES, spinCoin } from '../coin';
import { getAudioService } from '../audio/AudioService';
import { getMusicService } from '../audio/MusicService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { GOLD_HEX, hubProgress } from '../hubShared';

const ROW_W = 880;
const ROW_H = 92;
const SLOT_H = 110;
const AREA_TOP = 140;

function starString(stars: number): string {
  const n = Math.max(0, Math.min(3, stars));
  return '★'.repeat(n) + '☆'.repeat(3 - n);
}

/**
 * OriginChapterScene — one Origin Event chapter's level menu (spec
 * 2026-10-06), reached from its card on The Hub's EVENTS tab. The chapter's
 * name is the header ("ABOVE THE CLOUDS", "THE DARK SHADOWS"); the rows are
 * the Hub chapter screen's, paying the chapter's own coin instead of coins —
 * Light Hambi Coins above the clouds, Dark Hambi Coins in the shadows. The red
 * TEST button opens the chapter's own arena (spec 2026-10-08), where The
 * Moon's sits. BACK returns to the EVENTS tab. No Peaceful track.
 */
export class OriginChapterScene extends Phaser.Scene {
  private progress!: ProgressService;
  private scoreService!: LocalScoreService;
  private chapterId = 'shadows';

  constructor() {
    super({ key: 'OriginChapterScene' });
  }

  init(data: { chapter?: string }): void {
    if (data?.chapter) this.chapterId = data.chapter;
  }

  create(): void {
    const cx = 640;
    this.progress = hubProgress();
    this.scoreService = new LocalScoreService(saveStorage());
    const chapter: OriginChapterDef = findOriginChapter(this.chapterId) ?? ORIGIN_CHAPTERS[1];
    const shadows = chapter.id === 'shadows';
    const accentHex = shadows ? '#ff8a5c' : '#a8dcff';

    drawStarfield(this, 1280, 720);
    // The cover art, faint, down the right edge — this menu's own colour.
    if (this.textures.exists(chapter.art)) {
      this.add.image(1172, 420, chapter.art).setDisplaySize(176, 264).setAlpha(0.25).setDepth(0);
    }

    this.add
      .text(cx, 48, chapter.name.toUpperCase(), {
        fontSize: '38px',
        color: accentHex,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setName('origin-chapter-title');
    this.add
      .text(cx, 84, 'THE HUB · ORIGIN EVENT', { fontSize: '15px', color: '#9aa6ad' })
      .setOrigin(0.5)
      .setName('origin-chapter-subtitle');

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setName('origin-back')
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => this.scene.start('HubScene', { tab: 'events' }));

    // The chapter's coin balance where the chapter screens keep their coins.
    const light = originCurrencyOf(chapter.id) === 'light';
    const coinIcon = this.add
      .image(960, 50, light ? 'lightcoin' : 'darkcoin')
      .setDisplaySize(34, 34)
      .setDepth(5);
    spinCoin(this, coinIcon, light ? LIGHTCOIN_SPIN_FRAMES : DARKCOIN_SPIN_FRAMES);
    this.add
      .text(982, 50, `${light ? this.progress.lightCoins() : this.progress.darkCoins()}`, {
        fontSize: '22px',
        color: '#d7dbe2',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5)
      .setName(light ? 'origin-lightcoins' : 'origin-darkcoins');

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    // The chapter's Test Arena — red, where The Moon keeps its TEST.
    const testBtn = this.add
      .text(1180, 108, 'TEST', {
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: 'bold',
        backgroundColor: '#8a1a1a',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setName('origin-test')
      .setInteractive({ useHandCursor: true });
    testBtn.on('pointerover', () => testBtn.setBackgroundColor('#c0392b'));
    testBtn.on('pointerout', () => testBtn.setBackgroundColor('#8a1a1a'));
    testBtn.on('pointerup', () => {
      const arena = originArenaFor(chapter.id);
      this.scene.start('GameScene', { levelId: arena.id });
      this.scene.launch('HudScene', { levelId: arena.id, arena: chapter.id });
    });

    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => music.stop());

    if (chapter.levels.length === 0) {
      this.add
        .text(cx, 360, 'No levels yet…', {
          fontSize: '30px',
          color: accentHex,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setName('origin-empty');
      this.add
        .text(cx, 404, 'Check back soon.', { fontSize: '18px', color: '#9aa6ad' })
        .setOrigin(0.5);
    } else {
      chapter.levels.forEach((level, i) => {
        this.makeLevelRow(cx, AREA_TOP + SLOT_H * i + SLOT_H / 2, level, shadows);
      });
    }

    installDebugReturn(this);
  }

  private makeLevelRow(cx: number, rowY: number, level: LevelDef, shadows: boolean): void {
    const left = cx - ROW_W / 2;
    const top = rowY - ROW_H / 2;
    const right = cx + ROW_W / 2;
    const unlocked = isOriginLevelUnlocked(level.id, (id) => this.progress.stars(id));
    const stars = this.progress.stars(level.id);
    const trackColor = shadows ? 0xe0562a : 0x7fc8ff;
    const trackHex = shadows ? '#ff8a5c' : '#a8dcff';
    const accentNum = unlocked ? trackColor : 0x555a66;
    const accentHex = unlocked ? trackHex : '#7a818c';

    const panel = this.add.graphics().setDepth(1);
    const drawPanel = (hover: boolean) => {
      panel.clear();
      panel.fillStyle(0x15131f, unlocked ? (hover ? 0.95 : 0.8) : 0.6);
      panel.fillRoundedRect(left, top, ROW_W, ROW_H, 12);
      const lit = hover && unlocked;
      panel.lineStyle(lit ? 2 : 1, accentNum, lit ? 1 : 0.5);
      panel.strokeRoundedRect(left, top, ROW_W, ROW_H, 12);
      panel.fillStyle(accentNum, 1);
      panel.fillRoundedRect(left, top, 6, ROW_H, { tl: 12, bl: 12, tr: 0, br: 0 });
    };
    drawPanel(false);

    if (level.background && this.textures.exists(level.background)) {
      const wash = this.add
        .image(cx, rowY, level.background)
        .setDepth(1.5)
        .setAlpha(unlocked ? 0.38 : 0.15);
      wash.setScale(Math.max(ROW_W / wash.width, ROW_H / wash.height));
      const maskG = this.make.graphics();
      maskG.fillStyle(0xffffff);
      maskG.fillRoundedRect(left, top, ROW_W, ROW_H, 12);
      wash.setMask(maskG.createGeometryMask());
      wash.once('destroy', () => maskG.destroy());
    }

    const textX = left + 30;
    this.add
      .text(textX, rowY - 26, `LEVEL ${level.episode ?? level.id}`, {
        fontSize: '13px',
        color: accentHex,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(2);
    this.add
      .text(textX, rowY + 2, level.name, {
        fontSize: '27px',
        color: unlocked ? '#ffffff' : '#7a818c',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(2)
      .setName(`origin-level-${level.id}`);
    this.add
      .text(
        textX,
        rowY + 28,
        unlocked
          ? `${level.waves.length} waves · towers have ${level.towerHp ?? 3} HP`
          : 'Beat the previous level to unlock',
        { fontSize: '14px', color: unlocked ? '#9aa6ad' : '#c08a3e', fontStyle: 'italic' },
      )
      .setOrigin(0, 0.5)
      .setDepth(2);

    this.add
      .text(right - 24, rowY - 24, starString(stars), {
        fontSize: '18px',
        color: unlocked ? GOLD_HEX : '#555a66',
      })
      .setOrigin(1, 0.5)
      .setDepth(2);
    const scoreText = this.add
      .text(right - 24, rowY + 22, 'BEST: —', { fontSize: '16px', color: '#9aa6ad' })
      .setOrigin(1, 0.5)
      .setDepth(2);
    void this.scoreService
      .best(level.id)
      .then((best) => scoreText.setText(best > 0 ? `BEST: ${best}` : 'BEST: —'))
      .catch(() => {});

    // The prize: the chapter's coin, every win — for Dark Hambi Coins the
    // first-win figure until the level is won (The Quest for the Evil Orb:
    // 100, then 60). Right of the longest name ("The Quest for the Evil Orb").
    const rewardX = right - 330;
    const coin = this.add
      .image(rewardX, rowY, shadows ? 'darkcoin' : 'lightcoin')
      .setDisplaySize(28, 28)
      .setDepth(2);
    if (!unlocked) coin.setAlpha(0.5);
    const reward = shadows
      ? darkCoinRewardFor(level.id, stars === 0)
      : lightCoinRewardFor(level.id, stars === 0);
    this.add
      .text(rewardX + 18, rowY, `+${reward}`, {
        fontSize: '18px',
        color: unlocked ? '#d7dbe2' : '#7a818c',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(2)
      .setName(`origin-level-reward-${level.id}`);

    if (!unlocked) {
      this.add
        .text(right - 150, rowY, 'LOCKED', {
          fontSize: '20px',
          color: '#6a7078',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(2);
    }

    const hit = this.add.zone(cx, rowY, ROW_W, ROW_H).setOrigin(0.5).setDepth(3);
    if (unlocked) {
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => drawPanel(true));
      hit.on('pointerout', () => drawPanel(false));
      hit.on('pointerup', () => {
        this.scene.start('GameScene', { levelId: level.id });
        this.scene.launch('HudScene', { levelId: level.id });
      });
    }
  }
}
